<template>
    <div class="mb-[100px]">
        <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="getDomainStatusDotClass(reportedDomain)"
                        ></span>
                        <a
                            :href="
                                route('reports.show', {
                                    domain: reportedDomain.domain,
                                })
                            "
                            class="text-gray-800 hover:text-gray-900"
                        >
                            {{ reportedDomain.domain }}
                        </a>
                    </span>
                    <template v-if="source">
                        <svg
                            class="h-[6px] w-[6px] text-gray-800"
                            viewBox="0 0 6 6"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="0.9"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M2 1l2 2-2 2" />
                        </svg>
                        <span class="text-gray-800">{{ source }}</span>
                    </template>
                </span>
            </h1>

            <div>
                <DateRangeField
                    :date-range="dateRange"
                    :reported-domain="reportedDomain"
                    :display-full-block="true"
                    route-name="reports.show.source"
                    :route-params="{
                        domain: reportedDomain.domain,
                        source: source,
                    }"
                />
            </div>
        </div>

        <div class="mb-[42px]">
            <FreePlanReportsLimitBlock />
        </div>

        <StatsBar
            :stats="{ pass: reports.total.pass, fail: reports.total.fail }"
            :stats-before="null"
            :statistics="statistics"
        />

        <div class="mt-[22px] grid grid-cols-1 gap-[22px] lg:grid-cols-2">
            <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
                <h3 class="mb-4 text-sm font-semibold text-gray-900">
                    {{ $t("vue.dashboard_security_recommendations") }}
                </h3>
                <div class="space-y-4">
                    <div
                        class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-green-900">
                                {{
                                    $t(
                                        "vue.dashboard_security_recommendations_all_looking_good",
                                    )
                                }}
                            </p>
                            <p class="text-sm text-green-800">
                                {{
                                    $t(
                                        "vue.dashboard_security_recommendations_we_have",
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-blue-900">
                                {{ $t("vue.main_sender") }}
                            </p>
                            <p class="text-sm text-blue-800">
                                {{ mainSenderSummary }}
                            </p>
                        </div>
                    </div>
                    <div
                        v-if="spfAlignmentWarning"
                        class="flex items-start gap-4 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-yellow-900">
                                {{ $t("vue.spf_alignment") }}
                            </p>
                            <p class="text-sm text-yellow-800">
                                {{ spfAlignmentWarning }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
                <h3 class="mb-4 text-sm font-semibold text-gray-900">
                    {{ $t("vue.source_summary") }}
                </h3>
                <div class="space-y-4">
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.active_ip_addresses")
                        }}</span>
                        <span class="font-medium text-gray-900">{{
                            activeIpCount
                        }}</span>
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.average_dmarc_compliance")
                        }}</span>
                        <span
                            class="font-medium text-green-600"
                            :style="{
                                color: calculateColorByPercents(
                                    calculateCompliance(
                                        reports.total.pass,
                                        reports.total.fail,
                                    ),
                                ),
                            }"
                        >
                            {{
                                calculateCompliance(
                                    reports.total.pass,
                                    reports.total.fail,
                                )
                            }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.peak_volume")
                        }}</span>
                        <span class="font-medium text-gray-900">
                            {{
                                new Intl.NumberFormat().format(
                                    peakDay.pass + peakDay.fail,
                                )
                            }}
                            {{ $t("vue.emails") }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.authentication_method")
                        }}</span>
                        <span class="font-medium text-gray-900">{{
                            getPrimaryAuthMethod()
                        }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="reports.total.pass > 0 || reports.total.fail > 0"
            class="mt-[22px]"
        >
            <StatsChart
                :chart-data="reports.chartData"
                :header="t('vue.daily_reports_from_this_sender')"
            ></StatsChart>
        </div>

        <DynamicContentTable
            :title="$t('vue.ip_address_details')"
            :subtitle="$t('vue.auth_and_compliance_metrics_by_ip_address')"
            :rows="tableRows"
            :columns="tableColumns"
            :endpoint="
                () =>
                    route('reports.show.source', {
                        domain: reportedDomain.domain,
                        source: source,
                    })
            "
            :base-query="tableBaseQuery"
            :table="table"
            :pagination="pagination"
            :row-link="
                (row) =>
                    route('reports.show.ip', {
                        domain: reportedDomain.domain,
                        source: source,
                        ip: row.ip,
                    })
            "
            :search-placeholder="t('vue.search_sender')"
            :empty-text="
                page.props.hasFreePlan
                    ? t('vue.no_or_nullified_results')
                    : t('vue.no_results')
            "
            :per-page-label="t('vue.per_page')"
            :class="
                !(reports.total.pass > 0 || reports.total.fail > 0)
                    ? 'mt-[22px]'
                    : ''
            "
        >
            <template #row="{ row }">
                <td class="px-6 py-4">
                    <span
                        class="font-medium text-blue-600 transition-colors duration-150"
                    >
                        {{ row.ip }}
                        <IpFlagTooltip :geo="geoByIp[row.ip] ?? null" />
                    </span>
                </td>
                <td class="px-6 py-4">
                    <span class="text-gray-900">{{
                        new Intl.NumberFormat().format(row.sum)
                    }}</span>
                </td>
                <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                        <div class="w-20">
                            <div class="h-2 w-full rounded-full bg-gray-200">
                                <div
                                    class="h-2 rounded-full"
                                    :style="{
                                        width:
                                            calculateCompliancePercent(
                                                row.pass,
                                                row.fail,
                                            ) + '%',
                                        backgroundColor:
                                            calculateColorByPercents(
                                                calculateCompliance(
                                                    row.pass,
                                                    row.fail,
                                                ),
                                            ),
                                    }"
                                ></div>
                            </div>
                        </div>
                        <span
                            class="text-sm font-medium"
                            :style="{
                                color: calculateColorByPercents(
                                    calculateCompliance(row.pass, row.fail),
                                ),
                            }"
                            >{{ calculateCompliance(row.pass, row.fail) }}</span
                        >
                    </div>
                </td>
                <td class="px-6 py-4">
                    <span class="text-gray-900">{{
                        calculateCompliance(row.spf_valid, row.spf_invalid)
                    }}</span>
                </td>
                <td class="px-6 py-4">
                    <span class="text-gray-900">{{
                        calculateCompliance(row.dkim_valid, row.dkim_invalid)
                    }}</span>
                </td>
            </template>
        </DynamicContentTable>

        <div
            v-if="
                !page.props.hasFreePlan &&
                geoItems.some((item) => item.geo !== null)
            "
            class="mt-[22px] rounded-[12.8px] border border-gray-200 bg-white p-6"
        >
            <h3 class="mb-1 text-sm font-semibold text-gray-900">
                {{ $t("vue.ip_geo_map_header") }}
            </h3>
            <p class="mb-4 text-sm text-gray-500">
                {{ $t("vue.ip_geo_map_description") }}
            </p>
            <IpGeoMap :items="geoItems" />
        </div>
    </div>

    <FreePlanMaxReachedPopup />
</template>

<script lang="ts" setup>
import { computed, type PropType } from "vue";
import route from "ziggy-js";
import DynamicContentTable from "@/components/Content/DynamicContentTable.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import StatsBar from "@/Pages/Reports/components/StatsBar.vue";
import StatsChart from "@/Pages/Reports/components/StatsChart.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import FreePlanMaxReachedPopup from "@/components/Atomic/Dialogs/Popups/FreePlanMaxReachedPopup.vue";
import FreePlanReportsLimitBlock from "@/Pages/Reports/components/FreePlanReportsLimitBlock.vue";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";
import { getDomainStatusDotClass } from "@/utils/domainStatus";
import IpGeoMap from "@/components/Special/IpGeoMap.vue";
import IpFlagTooltip from "@/Pages/Reports/components/IpFlagTooltip.vue";

const page = usePage<IRhizomPageProps>();
const { t } = useTranslations();

interface ReportsIpRow {
    ip: string;
    pass: number;
    fail: number;
    sum: number;
    spf_valid: number;
    spf_invalid: number;
    dkim_valid: number;
    dkim_invalid: number;
}

interface ReportsData {
    total: {
        pass: number;
        fail: number;
        sum: number;
    };
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
    ips: Record<string, ReportsIpRow>;
}

interface IpGeoItem {
    ip: string;
    label: string;
    geo: {
        latitude: number;
        longitude: number;
        city: string | null;
        region: string | null;
        country: string | null;
        org: string | null;
        timezone: string | null;
    } | null;
    sources?: Array<{ domain: string; source: string }>;
}

interface ReportedDomain {
    domain: string;
    active: boolean;
    dmarcPolicy?: string;
    parked?: boolean;
}

type TableState = {
    q?: string;
    sort_by?: string | null;
    sort_dir?: "asc" | "desc" | null;
    per_page?: number;
    current_page?: number;
};

type Pagination = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
};

const props = defineProps({
    reports: {
        type: Object as PropType<ReportsData>,
        required: true,
    },
    source: {
        type: String,
        required: true,
    },
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    dateRange: {
        type: Object as PropType<{
            start: string;
            end: string;
        }>,
        required: true,
    },
    tableRows: {
        type: Array as PropType<ReportsIpRow[]>,
        required: true,
    },
    table: {
        type: Object as PropType<TableState>,
        required: true,
    },
    pagination: {
        type: Object as PropType<Pagination>,
        required: true,
    },
    geoItems: {
        type: Array as PropType<IpGeoItem[]>,
        required: true,
    },
});

const geoByIp = computed(
    (): Record<string, IpGeoItem["geo"]> =>
        Object.fromEntries(props.geoItems.map((item) => [item.ip, item.geo])),
);

const tableColumns = computed(() => [
    {
        key: "ip",
        label: t("vue.sender"),
        sortable: true,
        sortKey: "ip",
        colClass: "w-[22%]",
    },
    {
        key: "emails",
        label: t("vue.emails_reported"),
        sortable: true,
        sortKey: "emails",
        colClass: "w-[18%]",
    },
    {
        key: "dmarc",
        label: t("vue.dmarc_compliance"),
        sortable: true,
        sortKey: "dmarc",
        colClass: "w-[24%]",
    },
    {
        key: "spf",
        label: t("vue.spf_aligned"),
        sortable: true,
        sortKey: "spf",
        colClass: "w-[18%]",
    },
    {
        key: "dkim",
        label: t("vue.dkim_aligned"),
        sortable: true,
        sortKey: "dkim",
        colClass: "w-[18%]",
    },
]);

const tableBaseQuery = computed(() => ({
    start_date: props.dateRange.start,
    end_date: props.dateRange.end,
}));

const statistics = computed(() => {
    const total = props.reports.total.pass + props.reports.total.fail;

    return {
        totalEmailsReported: total,
        dmarcCompliant: props.reports.total.pass,
        dmarcNonCompliant: props.reports.total.fail,
        complianceRate: total
            ? Math.trunc((props.reports.total.pass / total) * 100)
            : 0,
    };
});

const peakDay = computed(() => {
    return props.reports.chartData.reduce(
        (peak, current) => {
            const currentTotal = current.pass + current.fail;
            const peakTotal = peak.pass + peak.fail;

            return currentTotal > peakTotal ? current : peak;
        },
        { day: "", pass: 0, fail: 0 },
    );
});

const activeIpCount = computed(() => Object.keys(props.reports.ips).length);

const mainSenderSummary = computed(() => {
    const entries = Object.entries(props.reports.ips).sort(
        (a, b) => (b[1].sum || 0) - (a[1].sum || 0),
    );
    const totalVolume = entries.reduce(
        (acc, [_, val]) => acc + (val.sum || 0),
        0,
    );

    if (entries.length === 0 || totalVolume === 0) {
        return t("vue.main_sender_summary_empty");
    }

    const percent = ((entries[0][1].sum / totalVolume) * 100).toFixed(1);

    return t("vue.main_sender_summary", { percent });
});

const spfAlignmentWarning = computed(() => {
    const totalIps = Object.keys(props.reports.ips).length;
    if (totalIps === 0) return null;

    const zeroSpfIps = Object.values(props.reports.ips).filter(
        (ip) => (ip.spf_valid || 0) === 0 && (ip.spf_invalid || 0) > 0,
    ).length;

    if (zeroSpfIps / totalIps < 0.1) return null;

    const percent = ((zeroSpfIps / totalIps) * 100).toFixed(1);

    return t("vue.spf_alignment_warning", { percent });
});

const calculateCompliancePercent = (pass: number, fail: number) => {
    const total = pass + fail;
    if (total === 0) return 0;
    return Math.round((pass / total) * 100);
};

const calculateCompliance = (pass: number, fail: number) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : "0%";
};

const getPrimaryAuthMethod = () => {
    let spfValid = 0;
    let spfTotal = 0;
    let dkimValid = 0;
    let dkimTotal = 0;

    for (const ip of Object.values(props.reports.ips)) {
        spfValid += ip.spf_valid || 0;
        spfTotal += (ip.spf_valid || 0) + (ip.spf_invalid || 0);
        dkimValid += ip.dkim_valid || 0;
        dkimTotal += (ip.dkim_valid || 0) + (ip.dkim_invalid || 0);
    }

    const spfRate = spfTotal ? spfValid / spfTotal : 0;
    const dkimRate = dkimTotal ? dkimValid / dkimTotal : 0;

    const delta = Math.abs(spfRate - dkimRate);

    if (delta < 0.05) {
        return t("vue.balanced_spf_dkim");
    }

    if (dkimRate > spfRate) {
        return t("vue.dkim_primary");
    }

    return t("vue.spf_primary");
};

const calculateColorByPercents = (percents: string) => {
    const percentNum = parseInt(percents.replace("%", ""), 10);

    if (percentNum > 75) {
        return "#15803D";
    }

    if (percentNum > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};
</script>
